import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function WebcamCapture({ onShot, onClose, onFail, testid }) {
  const video = useRef(null);
  const [stream, setStream] = useState(null);
  const [shot, setShot] = useState(null);
  useEffect(() => {
    let s, off = false;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 1280, height: 960 }, audio: false })
      .then((x) => { if (off) return x.getTracks().forEach((t) => t.stop()); s = x; setStream(x); })
      .catch(() => { if (off) return; toast.error("Camera not available. Allow camera access (lock icon in the address bar) or choose a selfie photo instead."); onFail(); });
    return () => { off = true; s?.getTracks().forEach((t) => t.stop()); };
  }, []); // eslint-disable-line
  const attach = (el) => { video.current = el; if (el && stream && el.srcObject !== stream) el.srcObject = stream; };
  const take = () => {
    const v = video.current;
    if (!v?.videoWidth) return toast.error("Camera is starting, try again in a second");
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => b && setShot(b), "image/jpeg", 0.9);
  };
  const use = () => { onShot(new File([shot], "selfie.jpg", { type: "image/jpeg" })); onClose(); };
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4" data-testid={`${testid}-webcam`}>
      <div className="w-full max-w-lg rounded-2xl bg-[#12141c] border border-white/10 p-4 space-y-3">
        <div className="flex items-center"><p className="font-semibold">Take a live selfie</p><button type="button" data-testid={`${testid}-webcam-close`} onClick={onClose} className="ml-auto p-2 rounded-full hover:bg-white/10"><X size={16} /></button></div>
        <div className="aspect-[4/3] rounded-xl overflow-hidden bg-black">
          {shot ? <img src={URL.createObjectURL(shot)} alt="Selfie preview" className="w-full h-full object-cover" /> : <video ref={attach} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" />}
        </div>
        {shot ? (
          <div className="flex gap-2">
            <button type="button" data-testid={`${testid}-webcam-retake`} onClick={() => setShot(null)} className="flex-1 py-2.5 rounded-full border border-white/15 flex items-center justify-center gap-2 text-sm"><RotateCcw size={15} />Retake</button>
            <button type="button" data-testid={`${testid}-webcam-use`} onClick={use} className="flex-1 py-2.5 rounded-full bg-[#ff5500] font-semibold text-sm">Use this photo</button>
          </div>
        ) : <button type="button" data-testid={`${testid}-webcam-take`} disabled={!stream} onClick={take} className="w-full py-2.5 rounded-full bg-[#ff5500] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40"><Camera size={16} />Take photo</button>}
      </div>
    </div>, document.body
  );
}
